import {Stack, Typography} from '@mui/material';
import React, {useEffect, useState} from 'react';

import {APIError} from 'data/errors/api-error';
import {HttpStatusCode} from 'data/protocols/http';
import {TypeLocalNameEnum} from 'domain/usecases';
import {GetHealthDistrictsParams} from 'domain/usecases/private/booking-flow/health/get-health-districts';
import {AppointmentBookingEnum, FinishWithoutCompleteFlowEnum} from 'main/analytics/gaTagsEnum';
import {AlertModal, CTAButtonFixed} from 'presentation/components';
import {FeaturesEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {
	BookingEmptyState,
	BookingErrorState,
	BookingLoadingState,
	BookingNotFoundListCard,
	BookingNotFoundState,
} from 'presentation/pages/private/booking/components';
import {BookingNotFoundVariantEnum} from 'presentation/pages/private/booking/components/BookingNotFoundDialog/types';
import {BookingNotFoundStateVariantEnum} from 'presentation/pages/private/booking/components/BookingNotFoundState/types';
import {FlowEnum, PATH_TITLE} from 'presentation/pages/private/booking/constants';

import {useMediaQueries, useQueryParams} from 'presentation/pages/private/hooks';
import {APIErrorsMessage} from 'presentation/pages/public/constants';
import {translations} from 'presentation/translations-new';
import {getPageHeaderTitle} from 'presentation/utils';

import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useHideSearchBarStore} from 'presentation/modules/booking/shared/store/useHideSearchBarStore';
import {DistrictsProps} from '../../types';
import {parseCommaSeparated} from '../../utils/parseCommaSeparated';
import {DistrictList} from '../DistrictList';
import {useGetHealthDistrictsQuery} from 'presentation/modules/booking/health/ui/pages/HealthBooking/steps/HealthDistrictSelection/queries/useGetHealthDistrictsQuery';
import {useRequestSchedulePermission} from 'presentation/modules/booking/shared/hooks/useRequestSchedulePermission';

const {
	loadingError,
	loadingState,
	emptySearchState,
	notFoundModal: {cardListText},
	emptyModal: {part1, part2},
} = translations['pt-br'].privateFlow.bookingFlow.districtFlow;
const {booking} = translations['pt-br'].others.sidebar;
const {all, goForward, selectAll, notice, understood} = translations['pt-br'].commons;

export function HealthDistricts({handlePrevStep, handleNextStep}: DistrictsProps) {
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const {SEARCH_PARAMS} = useQueryParams();
	const {query} = SEARCH_PARAMS;
	const bookingDistricts = parseCommaSeparated(bookingData?.districtName);
	const bookingCities = parseCommaSeparated(bookingData?.citiesNames);
	const setHideSearchBarState = useHideSearchBarStore(state => state.setHideSearchBar);
	const districtsInitialState = bookingDistricts.map((d, i) => `${d}-${bookingCities[i] ?? ''}`);
	const [selectedDistricts, setSelectedDistricts] = useState<string[]>(districtsInitialState);
	const [showEmptyDistrictsModal, setShowEmptyDistrictsModal] = useState(false);
	const {isEnabled: pacmanFlagIsEnabled} = useFeatureFlag(FeaturesEnum.PORTAL_FEATURE_PACMAN);
	const [isLoadingState, setIsLoadingState] = useState(true);
	const {isMobile} = useMediaQueries();
	const {
		handleGetHealthRequestSchedulePermission,
		healthRequestSchedulePermissionState,
		requestSchedulePermissionIsLoading,
	} = useRequestSchedulePermission();

	const pageTitle = getPageHeaderTitle({
		internalPaths: PATH_TITLE,
		mainPath: booking,
	});

	const districtParams: GetHealthDistrictsParams = {
		stateCode: bookingData.ufCode?.toUpperCase(),
		locationName: bookingData.cityName?.toUpperCase(),
		locationType: bookingData.typeLocalName || TypeLocalNameEnum.CIDADE,
		specialtyCode: Number(bookingData.specialtyCode),
	};

	const {
		refetch,
		isError: districtListHasError,
		isFetching: districtListIsLoading,
		data: districtList,
		error: districtListError,
	} = useGetHealthDistrictsQuery({params: districtParams, enabled: true});

	const districtListHasUnprocessableError =
		districtListHasError &&
		districtListError instanceof APIError &&
		districtListError.statusCode === HttpStatusCode.unprocessable;

	const specialtyNotAvailableOnline =
		districtListHasUnprocessableError &&
		districtListError?.message === APIErrorsMessage.NOT_ALLOWED_FOR_SPECIALTY;

	const shouldFetchPacman = districtListHasUnprocessableError && pacmanFlagIsEnabled;

	const NOT_FOUND =
		!districtListIsLoading &&
		(!districtList ||
			districtList.length === 0 ||
			(districtListHasError && districtListHasUnprocessableError));

	useEffect(() => {
		if (shouldFetchPacman) {
			handleGetHealthRequestSchedulePermission({
				city: bookingData.cityName?.toUpperCase(),
				specialtyCode: bookingData.specialtyCode,
				typeLocalName: bookingData.typeLocalName || TypeLocalNameEnum.CIDADE,
				stateCode: bookingData.ufCode?.toUpperCase(),
			});
		}
	}, [districtListHasUnprocessableError]);

	useEffect(() => {
		if (!districtListIsLoading && !requestSchedulePermissionIsLoading) {
			setIsLoadingState(false);
		}
	}, [districtListIsLoading, requestSchedulePermissionIsLoading]);

	useEffect(() => {
		if (NOT_FOUND) {
			setTimeout(() => {
				setHideSearchBarState(true);
			}, 0);
		}
	}, [NOT_FOUND, setHideSearchBarState]);

	if (districtListHasError && !districtListHasUnprocessableError) {
		return (
			<BookingErrorState
				showHeader={false}
				pageTitle={pageTitle}
				errorTitle={loadingError}
				handleBack={handlePrevStep}
				handleRefresh={refetch}
			/>
		);
	}

	if (isLoadingState) {
		return (
			<BookingLoadingState
				showHeader={false}
				pageTitle={pageTitle}
				loadingText={loadingState}
				handleBack={handlePrevStep}
			/>
		);
	}

	if (NOT_FOUND) {
		return (
			<BookingNotFoundState
				showHeader={false}
				flowType={FlowEnum.HEALTH}
				pageTitle={pageTitle}
				handleNext={handleNextStep}
				handleBack={handlePrevStep}
				variant={BookingNotFoundStateVariantEnum.DISTRICT}
				dataTagId={FinishWithoutCompleteFlowEnum.SEM_BAIRRO_DISPONIVEL}
				healthRequestSchedulePermission={healthRequestSchedulePermissionState}
				specialtyNotAvailableOnline={specialtyNotAvailableOnline}
			/>
		);
	}

	const handleSelectCard = (district: string, skipScroll?: boolean) => {
		if (district === `${all}-${selectAll}`) {
			const newAllSelected = !bookingData.allDistrictsSelected;
			const districts = districtList?.map(d => d.district);
			const cities = districtList?.map(d => d.city);

			setBookingData(prev => ({
				...prev,
				districtName: newAllSelected ? districts?.join(', ') : '',
				citiesNames: newAllSelected ? cities?.join(', ') : '',
				allDistrictsSelected: newAllSelected,
			}));

			setSelectedDistricts(
				newAllSelected ? (districtList?.map(d => `${d.district}-${d.city}`) ?? []) : [],
			);

			const shouldScrollToForwardButton =
				!isMobile && newAllSelected && districts && districts.length >= 12 && !skipScroll;

			if (shouldScrollToForwardButton) {
				document.getElementById('go-forward-button')?.scrollIntoView({behavior: 'smooth'});
			}
			return;
		}

		setSelectedDistricts(prevSelected => {
			const updatedSelected: string[] = prevSelected.includes(district)
				? prevSelected.filter(item => item !== district)
				: [...prevSelected, district];

			const districts = updatedSelected?.map(item => item?.split('-')[0]);
			const cities = updatedSelected?.map(item => item?.split('-')[1]);

			setBookingData(prev => ({
				...prev,
				districtName: districts.join(', '),
				citiesNames: cities.join(', '),
				allDistrictsSelected: updatedSelected.length === districtList?.length,
			}));

			return updatedSelected;
		});
	};

	const handleGoForward = () => {
		if (selectedDistricts.length === 0) {
			setShowEmptyDistrictsModal(true);
			return;
		}
		handleNextStep();
	};

	const FILTERED_DISTRICT_LIST =
		districtList?.filter(districtItem =>
			districtItem.district.toLowerCase().includes(query.trim().toLowerCase()),
		) ?? [];

	const showEmptyState = FILTERED_DISTRICT_LIST?.length === 0;
	if (showEmptyState) {
		return (
			<BookingEmptyState
				description={emptySearchState.emptyDistrictAll}
				flowType={FlowEnum.HEALTH}
				dataTagId={FinishWithoutCompleteFlowEnum.SEM_BAIRRO_ENCONTRADO}
			/>
		);
	}

	return (
		<Stack gap={2}>
			<DistrictList
				list={FILTERED_DISTRICT_LIST}
				handleSelectCard={handleSelectCard}
				selectedDistricts={selectedDistricts}
			/>
			<Stack mt={2} id="go-forward-button">
				<BookingNotFoundListCard
					openModalDataTagId={AppointmentBookingEnum.CLICK_NAO_ENCONTREI_BAIRRO_DESEJADO}
					modalHeaderModalDataTagId={
						AppointmentBookingEnum.VISUALIZOU_MODAL_NAO_ENCONTREI_BAIRRO_DESEJADO
					}
					modalFirstActionDataTagId={
						AppointmentBookingEnum.CLICK_CONSULTAR_REDE_ATENDIMENTO
					}
					modalCloseModalDataTagId={
						AppointmentBookingEnum.CLICK_FECHAR_MODAL_NAO_ENCONTREI_BAIRRO_DESEJADO
					}
					modalSecondActionDataTagId={
						AppointmentBookingEnum.CLICK_FALAR_COM_ATENDIMENTO_ONLINE
					}
					flowType={FlowEnum.HEALTH}
					customOrientationText={cardListText}
					variant={BookingNotFoundVariantEnum.DISTRICT}>
					<CTAButtonFixed.Root>
						<CTAButtonFixed.Action
							fullWidth
							onClick={handleGoForward}
							size="large"
							data-tag-id={AppointmentBookingEnum.CLICK_AVANCAR_SELECAO_BAIRROS}>
							{goForward}
						</CTAButtonFixed.Action>
					</CTAButtonFixed.Root>
				</BookingNotFoundListCard>
			</Stack>

			{showEmptyDistrictsModal ? (
				<AlertModal
					open={showEmptyDistrictsModal}
					headerTitle={notice}
					variant="info"
					onClose={() => setShowEmptyDistrictsModal(false)}
					buttonLabel={understood}
					slotProps={{
						paper: {
							style: {
								maxWidth: 420,
								width: '100%',
							},
						},
					}}
					subDescription={
						<Typography variant="h6" fontSize={18} textAlign="center" fontWeight={600}>
							{part1} <br /> {part2}
						</Typography>
					}
				/>
			) : null}
		</Stack>
	);
}
